import { useMemo, useEffect } from "react";
import { Flex, Text, Spin } from "@/base/components/index";
import { CheckIcon, CloseIcon } from "@/base/static/index";
import { useCheckboxGroupListStyles } from "./checkbox-group-list.styles";
import { CheckboxGroupListCheckboxGroup } from "./checkbox-group-list-checkbox-group";
import { CheckboxGroupListActions } from "./checkbox-group-list-actions";
import {
  CheckboxGroupListProps,
  CheckboxGroupListOptionType,
} from "../types/index";

import variablesFigmaColors from "@/base/global/variables/figma.colors.json";

export const CheckboxGroupList = ({
  options,
  showActions = true,
  autoSelectFirst = false,
  ...props
}: CheckboxGroupListProps) => {
  const { styles } = useCheckboxGroupListStyles();

  const handleClickUndoQuickSelect = () => {
    props.onChange?.([]);
  };

  const handleClickQuickSelect = () => {
    const currentValue = props.value || [];
    const maxLength = (props.maxLength || 1) - currentValue.length;

    props.onChange?.([
      ...currentValue,
      ...memoOptions
        .filter(
          (option) => !option.disabled && !currentValue.includes(option.value),
        )
        .map((option) => option.value)
        .slice(0, maxLength > 0 ? maxLength : 0),
    ]);
  };

  useEffect(() => {
    if (autoSelectFirst && options.length > 0) {
      const tm = setTimeout(() => handleClickQuickSelect(), 100);
      return () => clearTimeout(tm);
    }
  }, [options, autoSelectFirst]);

  const renderLabel = (option: CheckboxGroupListOptionType) => {
    return (
      <Flex vertical>
        <Text disabled={option.disabled}>{option.label}</Text>
        <Text disabled={option.disabled} type="secondary" size="small">
          {option.description}
        </Text>
        {option.statusItems && (
          <Flex gap={8}>
            {option.statusItems.map((statusItem, index) => (
              <Flex
                key={index}
                gap={4}
                align="center"
                className={styles.optionStatusItem}
              >
                {statusItem.type === "error" && (
                  <CloseIcon
                    color={variablesFigmaColors.ColorFigmaFunctionalAlert50}
                  />
                )}
                {statusItem.type === "success" && (
                  <CheckIcon
                    color={variablesFigmaColors.ColorFigmaFunctionalSuccess50}
                  />
                )}
                {statusItem.type === "loading" && <Spin size="small" />}
                <Text disabled size="small">
                  {statusItem.label}
                </Text>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    );
  };

  const sortOptions = (
    optionA: CheckboxGroupListOptionType,
    optionB: CheckboxGroupListOptionType,
  ) => {
    if (Number(optionA.order || 0) > Number(optionB.order || 0)) {
      return -1;
    } else if (Number(optionA.order || 0) < Number(optionB.order || 0)) {
      return 1;
    } else {
      return optionA.value > optionB.value ? 1 : -1;
    }
  };

  const getAvailableSelectValue = () => {
    const selectCount = props.value?.length || 0;
    const availableCount =
      options.filter((option) => !option.disabled).length - selectCount;

    return !!(availableCount && selectCount < (props.maxLength || 1));
  };

  const memoOptions: CheckboxGroupListOptionType[] = useMemo(
    () =>
      (options || []).map((item, index) => ({
        ...item,
        order: !item.disabled ? options.length + index : index,
      })),
    [options],
  );

  return (
    <Flex vertical gap={12} align="flex-start">
      {showActions && props.maxLength! > 1 && memoOptions?.length > 1 && (
        <CheckboxGroupListActions
          onClickUndoQuickSelect={handleClickUndoQuickSelect}
          onClickQuickSelect={handleClickQuickSelect}
          availableSelectValue={getAvailableSelectValue()}
        />
      )}
      <CheckboxGroupListCheckboxGroup
        {...props}
        options={memoOptions.sort(sortOptions).map((option) => ({
          ...option,
          label: renderLabel(option),
        }))}
      />
    </Flex>
  );
};
